/**
 * Report settings: one flat string map that is the page URL, the API query string and a saved
 * customisation (apps/api/src/reports/framework/params.ts). `period` is a relative preset
 * resolved here to `from` / `to` with the client's financial year, so a saved "This month"
 * report stays this month.
 */
export type Settings = Record<string, string>;

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
};
const parts = (s: string) => s.split('-').map(Number) as [number, number, number];

export interface PeriodPreset {
  key: string;
  label: string;
  /** For point-in-time reports only the end date matters. */
  asOfLabel?: string;
}

/** QBO's report period list (the subset accountants use), in QBO order. */
export const PERIOD_PRESETS: PeriodPreset[] = [
  { key: 'all', label: 'All dates' },
  { key: 'custom', label: 'Custom' },
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This week' },
  { key: 'this_week_to_date', label: 'This week to date' },
  { key: 'this_month', label: 'This month', asOfLabel: 'End of this month' },
  { key: 'this_month_to_date', label: 'This month to date' },
  { key: 'this_quarter', label: 'This quarter', asOfLabel: 'End of this quarter' },
  { key: 'this_quarter_to_date', label: 'This quarter to date' },
  { key: 'this_year', label: 'This financial year', asOfLabel: 'End of this financial year' },
  { key: 'this_year_to_date', label: 'This financial year to date' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_week', label: 'Last week' },
  { key: 'last_month', label: 'Last month', asOfLabel: 'End of last month' },
  { key: 'last_quarter', label: 'Last quarter', asOfLabel: 'End of last quarter' },
  { key: 'last_year', label: 'Last financial year', asOfLabel: 'End of last financial year' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

/** First day of the month `n` months from the month of `date`. */
function monthStart(date: string, n = 0): string {
  const [y, m] = parts(date);
  return iso(y, m + n, 1);
}
const dayBefore = (date: string) => {
  const [y, m, d] = parts(date);
  return iso(y, m, d - 1);
};
const addDays = (date: string, n: number) => {
  const [y, m, d] = parts(date);
  return iso(y, m, d + n);
};

/** Start of the financial year containing `date` (fyStartMonth 1-12). */
export function fyStart(date: string, fyStartMonth: number): string {
  const [y, m] = parts(date);
  return iso(m >= fyStartMonth ? y : y - 1, fyStartMonth, 1);
}

/** Start of the financial quarter containing `date`. */
function quarterStart(date: string, fyStartMonth: number): string {
  const start = fyStart(date, fyStartMonth);
  const [ys, ms] = parts(start);
  const [y, m] = parts(date);
  const months = (y - ys) * 12 + (m - ms);
  return monthStart(start, Math.floor(months / 3) * 3);
}

/** Dates for a preset; `from` null means "from the first entry". Null for custom. */
export function resolvePeriod(
  preset: string,
  today: string,
  fyStartMonth: number,
): { from: string | null; to: string } | null {
  const [y, m, d] = parts(today);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 Sunday (QBO weeks start Sunday)
  const weekStart = iso(y, m, d - dow);
  switch (preset) {
    case 'all':
      return { from: null, to: today };
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: dayBefore(today), to: dayBefore(today) };
    case 'this_week':
      return { from: weekStart, to: addDays(weekStart, 6) };
    case 'this_week_to_date':
      return { from: weekStart, to: today };
    case 'last_week':
      return { from: addDays(weekStart, -7), to: addDays(weekStart, -1) };
    case 'this_month':
      return { from: monthStart(today), to: dayBefore(monthStart(today, 1)) };
    case 'this_month_to_date':
      return { from: monthStart(today), to: today };
    case 'last_month':
      return { from: monthStart(today, -1), to: dayBefore(monthStart(today)) };
    case 'this_quarter': {
      const q = quarterStart(today, fyStartMonth);
      return { from: q, to: dayBefore(monthStart(q, 3)) };
    }
    case 'this_quarter_to_date':
      return { from: quarterStart(today, fyStartMonth), to: today };
    case 'last_quarter': {
      const q = monthStart(quarterStart(today, fyStartMonth), -3);
      return { from: q, to: dayBefore(monthStart(q, 3)) };
    }
    case 'this_year': {
      const s = fyStart(today, fyStartMonth);
      return { from: s, to: dayBefore(monthStart(s, 12)) };
    }
    case 'this_year_to_date':
      return { from: fyStart(today, fyStartMonth), to: today };
    case 'last_year': {
      const s = monthStart(fyStart(today, fyStartMonth), -12);
      return { from: s, to: dayBefore(monthStart(s, 12)) };
    }
    case 'last_7_days':
      return { from: addDays(today, -6), to: today };
    case 'last_30_days':
      return { from: addDays(today, -29), to: today };
    default:
      return null;
  }
}

/** Settings the page uses but the API does not need. */
const PAGE_ONLY = new Set(['period', 'saved']);

/**
 * The query the API runs: `period` resolved to dates (custom keeps the typed dates), empty
 * values dropped. `defaultPeriod` applies when neither a preset nor dates are set.
 */
export function apiSettings(
  s: Settings,
  ctx: { today: string; fyStartMonth: number; defaultPeriod: string },
): Settings {
  const out: Settings = {};
  for (const [k, v] of Object.entries(s)) if (v !== '' && !PAGE_ONLY.has(k)) out[k] = v;
  const preset = s['period'] ?? (s['from'] || s['to'] ? 'custom' : ctx.defaultPeriod);
  const r = resolvePeriod(preset, ctx.today, ctx.fyStartMonth);
  if (r) {
    delete out['from'];
    if (r.from) out['from'] = r.from;
    out['to'] = r.to;
  }
  return out;
}

export const toQuery = (s: Settings): string => new URLSearchParams(s).toString();

/** Splits a comma list setting. */
export const listOf = (s: Settings, key: string): string[] =>
  (s[key] ?? '').split(',').filter(Boolean);

/** Sets or clears a setting (a default value is removed, so URLs stay short). */
export function withSetting(s: Settings, key: string, value: string | null, dflt = ''): Settings {
  const next = { ...s };
  if (value === null || value === dflt) delete next[key];
  else next[key] = value;
  return next;
}

/** Reads a search object into settings, keeping string values only. */
export function parseSettings(search: Record<string, unknown>): Settings {
  const out: Settings = {};
  for (const [k, v] of Object.entries(search)) {
    if (typeof v === 'string') out[k] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
  }
  return out;
}
